import { DocumentReference, Timestamp } from 'firebase/firestore';
import { EventType } from '../utils/eventColors';
import { EventInput } from '@fullcalendar/core';
import type {ProductionEventColor} from '../utils/productionEventColors';

export type {ProductionEventColor};

export const DEPARTMENTS = {
  installation: 'Installation',
  customerService: 'Customer Service',
  assembly: 'Assembly',
  production: 'Production',
} as const;

/**
 * Type representing possible departments shown in the calendar UI.
 * Assembly employees use departmentString "Assembly" in Firestore.
 */
export type Department = typeof DEPARTMENTS[keyof typeof DEPARTMENTS];
export type TravelerType = typeof DEPARTMENTS.installation | typeof DEPARTMENTS.customerService | typeof DEPARTMENTS.production;
export type AssemblyTravelerStatusKind = 'ready' | 'coatings' | 'production';
export type TravelerVisualState = AssemblyTravelerStatusKind | 'overdue';

/**
 * Type representing possible event departments.
 */
export type EventDepartment = Department | 'all';
export type TimeOffDepartment = Department | 'Other';

export type IConflictCheckResponse = {
  safeToSave: boolean;
  lastModifiedBy?: ILastModifiedBy;
};

/**
 * Interface representing the properties of a user document.
 * Extends the UserAccessProperties interface.
 */
export interface IFirestoreUser {
  /** The first name of the user. */
  firstName: string;
  /** The last name of the user. */
  lastName: string;
  /** The email address of the user. */
  email: string;
  /** Reference to the employee document in Firestore. */
  employeeRef: DocumentReference | null;
}

/**
 * Interface representing the last modified information of a document.
 */
export interface ILastModifiedBy {
  id: string; // Unique identifier of the user who last modified the document
  time: Timestamp; // Timestamp of when the document was last modified
}

/**
 * Interface representing an event document in Firestore.
 */
export interface IFirestoreEvent {
  id: string; // Unique identifier for the event
  startDate: string; // Start date of the event in string format
  endDate: string; // End date of the event in string format
  revision?: number; // Monotonic revision used for stale write detection
  crew?: DocumentReference[]; // Optional array of DocumentReferences to crew members
  traveler?: DocumentReference[]; // Optional array of DocumentReferences to travelers
  customText?: string | null; // Optional custom text for the event
  notes?: string | null; // Optional notes for the event
  department: EventDepartment; // Backend event department value
  eventType?: EventType | null; // Optional type of the event
  lastModifiedBy: ILastModifiedBy; // Last modified information for the event
  searchIndexData?: ISearchIndexData; // display title for the event
  productionResourceId?: string | null; // Production resource assignment
  productionZone?: DocumentReference | null; // Production zone assignment for travelerless Production events
  productionEventColor?: ProductionEventColor | null; // Production event color assignment
}

/**
 * Interface representing the data structure for search index.
 */
export interface ISearchIndexData {
  /** The title of the search index entry. */
  title: string;
  /** The crew associated with the search index entry. */
  subtitle: string;
  /** The date range for the search index entry. */
  dateRange: string;
}

/**
 * Interface representing a holiday document in Firestore.
 */
export interface IFirestoreHoliday {
  name: string;
  date: string;
}

/**
 * Interface representing a holiday shown in the calendar.
 */
export interface IHoliday extends IFirestoreHoliday {
  id: string;
}

/**
 * Interface representing an employee document in Firestore.
 */
export interface IFirestoreEmployee {
  preferredName: string; // Preferred name of the employee
  employmentStatus: 'Active' | 'Inactive' | null; // Employment status of the employee
  jobTitle?: DocumentReference[] | null; // Optional array of DocumentReferences to job titles
  jobTitleString?: string | null; // Optional job title as a string
  departmentString?: string | null; // Optional department as a string
  seniorityString?: string | null; // Optional seniority level as a string
  primaryEmail?: string | null; // Primary email address
}

/**
 * Interface extending IFirestoreEmployee with additional properties.
 */
export interface IEmployee extends IFirestoreEmployee {
  id: string; // Unique identifier for the employee
  ref: DocumentReference; // DocumentReference to the employee document
}

/**
 * Interface representing a traveler document in Firestore.
 */
export interface IFirestoreTraveler {
  calendarDisplayName: string; // Display name for the traveler in the calendar
  workOrderType: string; // Work order type of the traveler
  description: string; // Description of the traveler
  status: string; // Status of the traveler
  events: DocumentReference[] | null; // Array of events associated with the traveler
  portalLink?: string; // Portal link for the traveler
  readyToShipDeadline?: string | null; // Ready-to-ship deadline used for Assembly traveler prioritization
  thisTravelerIsFor: TravelerType; // Department the traveler is associated with
  project?: DocumentReference[]; // DocumentReference to the project document
  fabCrew?: DocumentReference | null; // Fab crew reference used for Production calendar filtering
  projectCrews?: string[] | null; // Project crews used to determine whether Assembly work is ship-only
  requiresAssembly?: boolean | null; // Whether the traveler requires Assembly scheduling
  hideFromAssemblyTaskList?: boolean | null; // Whether to hide traveler from Assembly task list
  isValidAssemblyStatus?: boolean | null; // Whether traveler status is valid for Assembly scheduling
  itemTypeString?: string | null; // Item type for Assembly sidebar display
}

/**
 * Interface representing a project document in Firestore
 */
export interface IFirestoreProject {
  project: string;
  projectId: number;
  projectManager?: DocumentReference | DocumentReference[];
}

/**
 * Interface representing an event with additional properties for FullCalendar.
 */
export interface IEvent extends EventInput {
  id: string; // Unique identifier for the event
  start: Date | string; // Start date of the event
  end: Date | string | undefined; // End date of the event
  revision?: number; // Monotonic revision from Firestore
  crew: IEmployee | null; // Crew member associated with the event
  notes: string | null; // Optional notes for the event
  customText: string | null; // Optional custom text for the event
  department: EventDepartment; // Department associated with the event
  eventType: EventType | null; // Optional type of the event
  traveler: ITraveler | null; // Traveler associated with the event
  lastModifiedBy?: ILastModifiedBy; // Last modified information for the event
  productionResourceId?: string | null; // Production resource assignment
  productionZone?: DocumentReference | null; // Production zone assignment for travelerless Production events
  productionEventColor?: ProductionEventColor | null; // Production event color assignment
}

/**
 * Interface representing a Production scheduling resource stored in Firestore.
 */
export interface IFirestoreProductionResource {
  id: string;
  name: string;
  createdAt: Timestamp;
  archivedAt?: Timestamp | null;
  productionZone: DocumentReference;
  sortOrder: number;
}

/**
 * Interface representing a Production zone stored in Firestore.
 */
export interface IFirestoreProductionZone {
  productionZone: string;
  fabCrews?: DocumentReference[] | null;
}

/**
 * Interface representing a Production zone used by the app.
 */
export interface IProductionZone {
  id: string;
  name: string;
  ref: DocumentReference;
  fabCrews: DocumentReference[];
}

/**
 * Interface representing the calendar config document in Firestore.
 */
export interface IFirestoreCalendarConfig {
  productionResources?: IFirestoreProductionResource[] | null;
}

/**
 * Interface extending IEvent with a title property.
 */
export interface ICalendarEvent extends IEvent {
  title: string; // Title of the event
  resourceId?: string; // Resource ID for the event
  textColor: string; // Text color for the event
  backgroundColor: string; // Background color for the event
  borderColor: string; // Border color for the event
}

/**
 * Interface representing a traveler with additional properties.
 */
export interface ITraveler {
  displayName: string; // Display name of the traveler
  workOrderType: string; // Work order type of the traveler
  department: Department; // Department the traveler is associated with
  status: string; // Status of the traveler
  portalLink?: string; // Add the portalLink property
  readyToShipDeadline?: string | null;
  fabCrew?: DocumentReference | null;
  ref: DocumentReference; // DocumentReference to the traveler document
  id: string; // Unique identifier for the traveler
  events?: DocumentReference[] | null;
  wasPreviouslyScheduled?: boolean;
  projectCrews: string[];
  requiresAssembly: boolean;
  hideFromAssemblyTaskList: boolean;
  isValidAssemblyStatus: boolean;
  itemTypeString?: string | null;
}

/**
 * Interface representing a time off request stored in Firestore.
 *
 * This interface includes details about the employee and the requested time off period.
 */
export interface IFirestoreTimeOffRequest {
  /**
   * Reference to the employee document or employee ID.
   * Stored in Firestore as a reference, but the string representation is expected in the format from the vacations API.
   */
  employee: DocumentReference;
  /**
   * Start date of the time off request.
   * Date stored as ISO string in YYYY-MM-DD format.
   */
  startDate: string;
  /**
   * End date of the time off request.
   * Date stored as ISO string in YYYY-MM-DD format.
   */
  endDate: string;
  /**
   * Status of the time off request.
   */
  status: RequestStatus;
}

/**
 * Interface representing a time off request with additional employee details.
 */
export interface ITimeOffRequest extends IFirestoreTimeOffRequest {
  /**
   * Employee details associated with the time off request.
   */
  employeeName: string;
  /**
   * Department associated with the time off request.
   */
  department: TimeOffDepartment;
  backgroundColor: string; // Background color for the event
  borderColor: string; // Border color for the event
  textColor: string; // Text color for the event
}

/**
 * Status of a time off request.
 *
 * This type defines the possible statuses that a time off request can have.
 */
export type RequestStatus = 'pending' | 'approved' | 'denied' | 'canceled';
